import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Building2, GraduationCap, Users, DollarSign, AlertTriangle,
  Calendar, Search, Plus, RefreshCw, CheckCircle, XCircle,
  TrendingUp, Clock, FileText, Settings, LogOut, ChevronRight,
  MailCheck, User, Dumbbell, ShieldCheck, Trash2, AlertOctagon
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BackofficePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [organizaciones, setOrganizaciones] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [filtroTipo, setFiltroTipo] = useState('todas');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [showPagoDialog, setShowPagoDialog] = useState(false);
  const [orgSeleccionada, setOrgSeleccionada] = useState(null);
  const [pagoForm, setPagoForm] = useState({
    monto_clp: '', monto_usd: '', metodo_pago: 'transferencia', comprobante: '', notas: ''
  });
  const [guardando, setGuardando] = useState(false);
  
  // Nuevos estados para tabs de gestión
  const [clubes, setClubes] = useState([]);
  const [academias, setAcademias] = useState([]);
  const [entrenadores, setEntrenadores] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loadingClubes, setLoadingClubes] = useState(false);
  const [loadingAcademias, setLoadingAcademias] = useState(false);
  const [loadingEntrenadores, setLoadingEntrenadores] = useState(false);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  
  // Filtros
  const [filtroClubes, setFiltroClubes] = useState({ search: '', plan: 'todos' });
  const [filtroAcademias, setFiltroAcademias] = useState({ search: '', plan: 'todos' });
  const [filtroEntrenadores, setFiltroEntrenadores] = useState({ search: '', estado: 'todos' });
  const [filtroUsuarios, setFiltroUsuarios] = useState({ search: '', rol: 'todos', verificado: 'todos' });

  // Estados para eliminación con doble confirmación
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1); // 1 = primera confirmación, 2 = segunda confirmación
  const [deleteTarget, setDeleteTarget] = useState(null); // { tipo: 'club'|'academia'|'entrenador'|'usuario', id, nombre, info }
  const [deleteInfo, setDeleteInfo] = useState(null); // Info de registros a eliminar
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [confirmText, setConfirmText] = useState(''); // Texto de confirmación para paso 2
  
  // Estados para verificación manual de enrolamiento
  const [pendientesVerificacion, setPendientesVerificacion] = useState([]);
  const [loadingVerificacion, setLoadingVerificacion] = useState(false);
  const [verificandoId, setVerificandoId] = useState(null);
  const [showVerificacionDialog, setShowVerificacionDialog] = useState(false);
  const [imagenAmpliadaUrl, setImagenAmpliadaUrl] = useState(null);
  const [personaVerificar, setPersonaVerificar] = useState(null);
  const [verificacionForm, setVerificacionForm] = useState({ nombre: '', apellido: '', fecha_nacimiento: '', motivo_rechazo: '' });

  // Estado para eliminar enrolamiento
  const [rutEliminar, setRutEliminar] = useState('');
  const [eliminandoRut, setEliminandoRut] = useState(false);

  const handleEliminarEnrolamiento = async () => {
    const rutLimpio = rutEliminar.replace(/\./g, '').replace(/-/g, '').trim();
    if (rutLimpio.length < 8) { toast.error('RUT inválido'); return; }
    if (!window.confirm(`¿Seguro que deseas eliminar el enrolamiento del RUT ${rutEliminar}? Esta acción no se puede deshacer.`)) return;
    setEliminandoRut(true);
    try {
      const res = await axios.delete(`${API}/enrolamiento/${rutLimpio}`, { withCredentials: true });
      toast.success(res.data.message);
      setRutEliminar('');
      loadPendientesVerificacion();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setEliminandoRut(false);
    }
  };

  // Estados para diagnóstico y limpieza de cuotas
  const [showDiagnosticoDialog, setShowDiagnosticoDialog] = useState(false);
  const [diagnosticoCuotas, setDiagnosticoCuotas] = useState(null);
  const [loadingDiagnostico, setLoadingDiagnostico] = useState(false);
  const [loadingLimpieza, setLoadingLimpieza] = useState(false);

  // Estado para diagnóstico de integrantes de club
  const [showDiagIntegrantes, setShowDiagIntegrantes] = useState(false);
  const [diagIntegrantes, setDiagIntegrantes] = useState(null);
  const [loadingDiagIntegrantes, setLoadingDiagIntegrantes] = useState(false);
  const [diagClubId, setDiagClubId] = useState(null);
  const [asignandoCategoria, setAsignandoCategoria] = useState(null);

  // Estado para gestionar plan de organización
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [planOrg, setPlanOrg] = useState(null);
  const [planForm, setPlanForm] = useState({ plan: 'pro', plan_valido_hasta: '', motivo: '' });
  const [guardandoPlan, setGuardandoPlan] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
      loadData();
    } catch (error) {
      toast.error('Acceso denegado');
      navigate('/');
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [dashRes, orgsRes, pagosRes] = await Promise.all([
        axios.get(`${API}/backoffice/dashboard`, { withCredentials: true }),
        axios.get(`${API}/backoffice/organizaciones?tipo=${filtroTipo}`, { withCredentials: true }),
        axios.get(`${API}/backoffice/pagos`, { withCredentials: true })
      ]);
      setDashboard(dashRes.data);
      setOrganizaciones(orgsRes.data);
      setPagos(pagosRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('No tienes permisos para acceder al backoffice');
        navigate('/');
      } else {
        toast.error('Error al cargar datos');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrarPago = async () => {
    if (!orgSeleccionada) return;
    if (!pagoForm.monto_clp && !pagoForm.monto_usd) {
      toast.error('Ingresa al menos un monto');
      return;
    }
    
    setGuardando(true);
    try {
      const res = await axios.post(`${API}/backoffice/registrar-pago`, {
        tipo: orgSeleccionada.tipo,
        organizacion_id: orgSeleccionada.id,
        monto_clp: parseInt(pagoForm.monto_clp) || 0,
        monto_usd: parseInt(pagoForm.monto_usd) || 0,
        metodo_pago: pagoForm.metodo_pago,
        comprobante: pagoForm.comprobante,
        notas: pagoForm.notas
      }, { withCredentials: true });
      
      toast.success(res.data.message);
      setShowPagoDialog(false);
      setPagoForm({ monto_clp: '', monto_usd: '', metodo_pago: 'transferencia', comprobante: '', notas: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar pago');
    } finally {
      setGuardando(false);
    }
  };

  const handleVerificarLicencias = async () => {
    try {
      const res = await axios.post(`${API}/backoffice/verificar-licencias`, {}, { withCredentials: true });
      toast.success(`Verificación completada. Clubes: ${res.data.clubes_downgraded}, Academias: ${res.data.academias_downgraded} pasados a Free`);
      loadData();
    } catch (error) {
      toast.error('Error al verificar licencias');
    }
  };

  const handleVerificarEmails = async () => {
    try {
      const res = await axios.post(`${API}/backoffice/verificar-emails`, {}, { withCredentials: true });
      toast.success(res.data.message);
    } catch (error) {
      toast.error('Error al verificar emails');
    }
  };

  // Funciones de diagnóstico y limpieza de cuotas
  const handleDiagnosticoCuotas = async () => {
    setLoadingDiagnostico(true);
    setShowDiagnosticoDialog(true);
    try {
      const res = await axios.get(`${API}/backoffice/diagnostico-cuotas`, { withCredentials: true });
      setDiagnosticoCuotas(res.data);
    } catch (error) {
      toast.error('Error al diagnosticar cuotas');
      setShowDiagnosticoDialog(false);
    } finally {
      setLoadingDiagnostico(false);
    }
  };

  const handleLimpiarCuotasHuerfanas = async () => {
    if (!window.confirm('¿Estás seguro? Se eliminarán todas las cuotas huérfanas (sin integrante/alumno o club/academia válido).')) {
      return;
    }
    setLoadingLimpieza(true);
    try {
      const res = await axios.post(`${API}/backoffice/limpiar-cuotas-huerfanas`, {}, { withCredentials: true });
      toast.success(`Limpieza completada: ${res.data.total_eliminadas} cuotas eliminadas (${formatMoney(res.data.total_monto_eliminado)} en morosidades ficticias)`);
      setDiagnosticoCuotas(null);
      setShowDiagnosticoDialog(false);
    } catch (error) {
      toast.error('Error al limpiar cuotas');
    } finally {
      setLoadingLimpieza(false);
    }
  };

  const handleLimpiarCuotasClub = async (clubId, clubNombre) => {
    if (!window.confirm(`¿Estás seguro? Se eliminarán TODAS las cuotas pendientes del club "${clubNombre}".`)) {
      return;
    }
    setLoadingLimpieza(true);
    try {
      const res = await axios.post(`${API}/backoffice/limpiar-cuotas-club/${clubId}`, {}, { withCredentials: true });
      toast.success(res.data.message);
      loadClubes();
    } catch (error) {
      toast.error('Error al limpiar cuotas del club');
    } finally {
      setLoadingLimpieza(false);
    }
  };

  const handleLimpiarCuotasAcademia = async (academiaId, academiaNombre) => {
    if (!window.confirm(`¿Estás seguro? Se eliminarán TODAS las cuotas pendientes de la academia "${academiaNombre}".`)) {
      return;
    }
    setLoadingLimpieza(true);
    try {
      const res = await axios.post(`${API}/backoffice/limpiar-cuotas-academia/${academiaId}`, {}, { withCredentials: true });
      toast.success(res.data.message);
      loadAcademias();
    } catch (error) {
      toast.error('Error al limpiar cuotas de la academia');
    } finally {
      setLoadingLimpieza(false);
    }
  };

  const handleRepararRolesUsuarios = async () => {
    try {
      const res = await axios.post(`${API}/backoffice/reparar-roles-usuarios`, {}, { withCredentials: true });
      if (res.data.total_reparados > 0) {
        toast.success(`Roles reparados: ${res.data.usuarios_club_reparados} en clubes, ${res.data.usuarios_academia_reparados} en academias`);
      } else {
        toast.info('No se encontraron usuarios con roles faltantes');
      }
    } catch (error) {
      toast.error('Error al reparar roles');
    }
  };

  const handleLimpiarDuplicados = async () => {
    try {
      const res = await axios.post(`${API}/backoffice/limpiar-duplicados`, {}, { withCredentials: true });
      if (res.data.total_corregidos > 0) {
        toast.success(`Limpieza completada: ${res.data.usuarios_duplicados_eliminados} duplicados eliminados, ${res.data.roles_admin_corregidos_club + res.data.roles_admin_corregidos_academia} roles corregidos`);
        // Recargar datos
        loadUsuarios();
        loadClubes();
        loadAcademias();
      } else {
        toast.info('No se encontraron duplicados ni inconsistencias');
      }
    } catch (error) {
      toast.error('Error al limpiar duplicados');
    }
  };

  // Funciones de carga para nuevos tabs
  const loadClubes = async () => {
    setLoadingClubes(true);
    try {
      const res = await axios.get(`${API}/backoffice/clubes`, { withCredentials: true });
      setClubes(res.data);
    } catch (error) {
      toast.error('Error al cargar clubes');
    } finally {
      setLoadingClubes(false);
    }
  };

  const handleDiagIntegrantes = async (clubId) => {
    setDiagClubId(clubId);
    setLoadingDiagIntegrantes(true);
    setShowDiagIntegrantes(true);
    setDiagIntegrantes(null);
    try {
      const res = await axios.get(`${API}/backoffice/clubes/${clubId}/diagnostico-integrantes`, { withCredentials: true });
      setDiagIntegrantes(res.data);
    } catch (error) {
      toast.error('Error al diagnosticar integrantes');
    } finally {
      setLoadingDiagIntegrantes(false);
    }
  };

  const handleAsignarCategoria = async (integranteId, categoriaId) => {
    if (!diagClubId) return;
    setAsignandoCategoria(integranteId);
    try {
      await axios.post(`${API}/backoffice/clubes/${diagClubId}/integrantes/${integranteId}/asignar-categoria`, 
        { categoria_id: categoriaId }, { withCredentials: true });
      toast.success('Categoria asignada');
      handleDiagIntegrantes(diagClubId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar');
    } finally {
      setAsignandoCategoria(null);
    }
  };

  const abrirGestionPlan = (org, tipo) => {
    setPlanOrg({ ...org, tipo });
    setPlanForm({
      plan: org.plan || 'free',
      plan_valido_hasta: org.plan_valido_hasta || '',
      motivo: ''
    });
    setShowPlanDialog(true);
  };

  const handleGuardarPlan = async () => {
    if (!planOrg) return;
    setGuardandoPlan(true);
    try {
      await axios.put(`${API}/backoffice/organizaciones/${planOrg.tipo}/${planOrg.id}/plan`, {
        plan: planForm.plan,
        plan_valido_hasta: planForm.plan_valido_hasta || null,
        motivo: planForm.motivo
      }, { withCredentials: true });
      toast.success(`Plan actualizado: ${planForm.plan.toUpperCase()} hasta ${planForm.plan_valido_hasta || 'sin fecha'}`);
      setShowPlanDialog(false);
      if (planOrg.tipo === 'club') loadClubes();
      else loadAcademias();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar plan');
    } finally {
      setGuardandoPlan(false);
    }
  };

  const loadAcademias = async () => {
    setLoadingAcademias(true);
    try {
      const res = await axios.get(`${API}/backoffice/academias`, { withCredentials: true });
      setAcademias(res.data);
    } catch (error) {
      toast.error('Error al cargar academias');
    } finally {
      setLoadingAcademias(false);
    }
  };

  const loadEntrenadores = async () => {
    setLoadingEntrenadores(true);
    try {
      const res = await axios.get(`${API}/backoffice/entrenadores`, { withCredentials: true });
      setEntrenadores(res.data);
    } catch (error) {
      toast.error('Error al cargar entrenadores');
    } finally {
      setLoadingEntrenadores(false);
    }
  };

  const loadUsuarios = async () => {
    setLoadingUsuarios(true);
    try {
      const res = await axios.get(`${API}/backoffice/usuarios`, { withCredentials: true });
      setUsuarios(res.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoadingUsuarios(false);
    }
  };

  // Funciones de verificación manual de enrolamiento
  const loadPendientesVerificacion = async () => {
    setLoadingVerificacion(true);
    try {
      const res = await axios.get(`${API}/enrolamiento/pendientes-verificacion`, { withCredentials: true });
      setPendientesVerificacion(res.data);
    } catch (error) {
      toast.error('Error al cargar pendientes de verificación');
    } finally {
      setLoadingVerificacion(false);
    }
  };

  const abrirVerificacion = (persona) => {
    setPersonaVerificar(persona);
    setVerificacionForm({
      nombre: persona.nombre || '',
      apellido: persona.apellido || '',
      fecha_nacimiento: persona.fecha_nacimiento || '',
      motivo_rechazo: ''
    });
    setShowVerificacionDialog(true);
  };

  const handleVerificarPersona = async (accion) => {
    if (!personaVerificar) return;
    setVerificandoId(personaVerificar.id);
    try {
      const body = { accion };
      if (accion === 'verificar') {
        body.nombre = verificacionForm.nombre;
        body.apellido = verificacionForm.apellido;
        body.fecha_nacimiento = verificacionForm.fecha_nacimiento;
      } else {
        body.motivo_rechazo = verificacionForm.motivo_rechazo;
      }
      await axios.post(`${API}/enrolamiento/${personaVerificar.id}/verificar-manual`, body, { withCredentials: true });
      toast.success(accion === 'verificar' ? 'Persona verificada' : 'Persona rechazada');
      setShowVerificacionDialog(false);
      setPersonaVerificar(null);
      loadPendientesVerificacion();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en verificación');
    } finally {
      setVerificandoId(null);
    }
  };

  const handleVerificarEmailUsuario = async (userId, email) => {
    try {
      await axios.post(`${API}/backoffice/usuarios/${userId}/verificar-email`, {}, { withCredentials: true });
      toast.success(`Email ${email} verificado correctamente`);
      loadUsuarios();
    } catch (error) {
      toast.error('Error al verificar email');
    }
  };

  // ==================== FUNCIONES DE ELIMINACIÓN ====================
  
  const iniciarEliminacion = async (tipo, id, nombre, extraInfo = {}) => {
    setDeleteTarget({ tipo, id, nombre, ...extraInfo });
    setDeleteStep(1);
    setConfirmText('');
    setDeleteInfo(null);
    
    // Para clubs y academias, cargar info de lo que se eliminará
    if (tipo === 'club' || tipo === 'academia') {
      try {
        const res = await axios.get(`${API}/backoffice/${tipo}s/${id}/info-eliminacion`, { withCredentials: true });
        setDeleteInfo(res.data);
      } catch (error) {
        console.error('Error al cargar info de eliminación:', error);
      }
    }
    
    setShowDeleteDialog(true);
  };

  const handleConfirmarPaso1 = () => {
    setDeleteStep(2);
  };

  const handleConfirmarPaso2 = async () => {
    if (!deleteTarget) return;
    
    // Verificar texto de confirmación
    const textoEsperado = `ELIMINAR ${deleteTarget.nombre}`.toUpperCase();
    if (confirmText.toUpperCase() !== textoEsperado) {
      toast.error('El texto de confirmación no coincide');
      return;
    }
    
    setLoadingDelete(true);
    try {
      const endpoint = `${API}/backoffice/${deleteTarget.tipo}s/${deleteTarget.id}`;
      const res = await axios.delete(endpoint, { withCredentials: true });
      
      toast.success(res.data.message);
      
      // Recargar la lista correspondiente
      if (deleteTarget.tipo === 'club') {
        loadClubes();
        loadData(); // Actualizar dashboard
      } else if (deleteTarget.tipo === 'academia') {
        loadAcademias();
        loadData();
      } else if (deleteTarget.tipo === 'entrenador') {
        loadEntrenadores();
      } else if (deleteTarget.tipo === 'usuario') {
        loadUsuarios();
      }
      
      setShowDeleteDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setLoadingDelete(false);
    }
  };

  const cerrarDeleteDialog = () => {
    setShowDeleteDialog(false);
    setDeleteTarget(null);
    setDeleteStep(1);
    setConfirmText('');
    setDeleteInfo(null);
  };

  const abrirDialogPago = (org) => {
    setOrgSeleccionada(org);
    setPagoForm({ monto_clp: '', monto_usd: '', metodo_pago: 'transferencia', comprobante: '', notas: '' });
    setShowPagoDialog(true);
  };

  const formatMoney = (amount, currency = 'CLP') => {
    if (currency === 'USD') {
      return `$${amount} USD`;
    }
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(amount || 0);
  };

  const getEstadoLicencia = (org) => {
    if (org.plan !== 'pro') return { estado: 'free', color: 'bg-slate-500', texto: 'Free' };
    if (!org.plan_valido_hasta) return { estado: 'pro', color: 'bg-emerald-500', texto: 'Pro (sin fecha)' };
    
    const hoy = new Date();
    const expiracion = new Date(org.plan_valido_hasta + 'T23:59:59');
    const diasRestantes = Math.ceil((expiracion - hoy) / (1000 * 60 * 60 * 24));
    
    if (diasRestantes < -10) return { estado: 'vencido', color: 'bg-red-500', texto: 'Vencido (pasar a Free)' };
    if (diasRestantes < 0) return { estado: 'prorroga', color: 'bg-orange-500', texto: `Prórroga (${10 + diasRestantes} días)` };
    if (diasRestantes <= 7) return { estado: 'por_vencer', color: 'bg-yellow-500', texto: `Vence en ${diasRestantes} días` };
    return { estado: 'activo', color: 'bg-emerald-500', texto: `Válido hasta ${org.plan_valido_hasta}` };
  };

  const filteredOrgs = organizaciones.filter(org => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return org.nombre?.toLowerCase().includes(q) || 
           org.admin_info?.email?.toLowerCase().includes(q) ||
           org.admin_info?.name?.toLowerCase().includes(q);
  });

  // Filtros para nuevos tabs
  const filteredClubes = clubes.filter(club => {
    const q = filtroClubes.search.toLowerCase();
    const matchSearch = !q || 
      club.nombre?.toLowerCase().includes(q) || 
      club.admin_info?.email?.toLowerCase().includes(q) ||
      club.codigo_pago?.toLowerCase().includes(q);
    const matchPlan = filtroClubes.plan === 'todos' || club.plan === filtroClubes.plan;
    return matchSearch && matchPlan;
  });

  const filteredAcademias = academias.filter(academia => {
    const q = filtroAcademias.search.toLowerCase();
    const matchSearch = !q || 
      academia.nombre?.toLowerCase().includes(q) || 
      academia.admin_info?.email?.toLowerCase().includes(q) ||
      academia.codigo_pago?.toLowerCase().includes(q);
    const matchPlan = filtroAcademias.plan === 'todos' || academia.plan === filtroAcademias.plan;
    return matchSearch && matchPlan;
  });

  const filteredEntrenadores = entrenadores.filter(entrenador => {
    const q = filtroEntrenadores.search.toLowerCase();
    const matchSearch = !q || 
      entrenador.nombre?.toLowerCase().includes(q) ||
      entrenador.apellido?.toLowerCase().includes(q) ||
      entrenador.email?.toLowerCase().includes(q) ||
      entrenador.academia_nombre?.toLowerCase().includes(q);
    const matchEstado = filtroEntrenadores.estado === 'todos' || entrenador.estado === filtroEntrenadores.estado;
    return matchSearch && matchEstado;
  });

  const filteredUsuarios = usuarios.filter(usuario => {
    const q = filtroUsuarios.search.toLowerCase();
    const matchSearch = !q || 
      usuario.name?.toLowerCase().includes(q) ||
      usuario.email?.toLowerCase().includes(q);
    const matchRol = filtroUsuarios.rol === 'todos' || usuario.role === filtroUsuarios.rol;
    const matchVerificado = filtroUsuarios.verificado === 'todos' || 
      (filtroUsuarios.verificado === 'si' && usuario.email_verified) ||
      (filtroUsuarios.verificado === 'no' && !usuario.email_verified);
    return matchSearch && matchRol && matchVerificado;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/95 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Settings className="w-8 h-8 text-emerald-400" />
            <div>
              <h1 className="text-xl font-bold">Kinosis Backoffice</h1>
              <p className="text-sm text-slate-400">{user?.email}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/')}>
            <LogOut className="w-4 h-4 mr-2" /> Salir
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs defaultValue="dashboard" className="space-y-6">
          <TabsList className="bg-slate-800 border-slate-700 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="organizaciones">Organizaciones</TabsTrigger>
            <TabsTrigger value="clubes" onClick={() => !clubes.length && loadClubes()}>Clubes</TabsTrigger>
            <TabsTrigger value="academias" onClick={() => !academias.length && loadAcademias()}>Academias</TabsTrigger>
            <TabsTrigger value="entrenadores" onClick={() => !entrenadores.length && loadEntrenadores()}>Entrenadores</TabsTrigger>
            <TabsTrigger value="usuarios" onClick={() => !usuarios.length && loadUsuarios()}>Usuarios</TabsTrigger>
            <TabsTrigger value="verificacion" onClick={() => !pendientesVerificacion.length && loadPendientesVerificacion()}>Verificacion</TabsTrigger>
            <TabsTrigger value="pagos">Historial de Pagos</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="p-4 bg-slate-800 border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <Users className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Total Clubes</p>
                    <p className="text-2xl font-bold">{dashboard?.estadisticas?.total_clubes || 0}</p>
                    <p className="text-xs text-emerald-400">{dashboard?.estadisticas?.clubes_pro || 0} Pro</p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-4 bg-slate-800 border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-500/20 rounded-lg">
                    <GraduationCap className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Total Academias</p>
                    <p className="text-2xl font-bold">{dashboard?.estadisticas?.total_academias || 0}</p>
                    <p className="text-xs text-emerald-400">{dashboard?.estadisticas?.academias_pro || 0} Pro</p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-4 bg-slate-800 border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-500/20 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Por Vencer</p>
                    <p className="text-2xl font-bold">
                      {(dashboard?.alertas?.clubes_por_vencer?.length || 0) + (dashboard?.alertas?.academias_por_vencer?.length || 0)}
                    </p>
                    <p className="text-xs text-yellow-400">Próximos 7 días</p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-4 bg-slate-800 border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-500/20 rounded-lg">
                    <XCircle className="w-6 h-6 text-red-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Vencidos</p>
                    <p className="text-2xl font-bold">
                      {(dashboard?.alertas?.clubes_vencidos?.length || 0) + (dashboard?.alertas?.academias_vencidas?.length || 0)}
                    </p>
                    <p className="text-xs text-red-400">Pasar a Free</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Herramientas de Mantenimiento */}
            <Card className="p-4 bg-slate-800 border-slate-700">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5 text-slate-400" />
                Herramientas de Mantenimiento
              </h3>
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" onClick={handleVerificarEmails} className="border-amber-600/50 text-amber-400 hover:bg-amber-600/20">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Verificar Todos los Emails
                </Button>
                <Button variant="outline" onClick={handleDiagnosticoCuotas} className="border-red-600/50 text-red-400 hover:bg-red-600/20">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Diagnóstico de Morosidades
                </Button>
                <Button variant="outline" onClick={handleRepararRolesUsuarios} className="border-blue-600/50 text-blue-400 hover:bg-blue-600/20">
                  <Users className="w-4 h-4 mr-2" />
                  Reparar Roles Usuarios
                </Button>
                <Button variant="outline" onClick={handleLimpiarDuplicados} className="border-purple-600/50 text-purple-400 hover:bg-purple-600/20">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Limpiar Duplicados
                </Button>
                <div className="w-full mt-3 space-y-2">
                  <p className="text-xs text-slate-500">
                    <strong className="text-amber-400">Verificar Emails:</strong> Marca todos los usuarios como "email verificado". Usar si hay usuarios que no pueden hacer login después de aceptar invitaciones.
                  </p>
                  <p className="text-xs text-slate-500">
                    <strong className="text-red-400">Diagnóstico de Morosidades:</strong> Detecta y limpia cuotas huérfanas que generan morosidades ficticias (cuotas de integrantes o clubes que ya no existen).
                  </p>
                  <p className="text-xs text-slate-500">
                    <strong className="text-blue-400">Reparar Roles:</strong> Corrige usuarios de clubes/academias que no tienen rol asignado (asigna "Solo Lectura" por defecto).
                  </p>
                  <p className="text-xs text-slate-500">
                    <strong className="text-purple-400">Limpiar Duplicados:</strong> Elimina usuarios duplicados y corrige roles de administradores que aparecen como "Solo Lectura".
                  </p>
                </div>
              </div>
            </Card>

            {/* Alertas */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Por Vencer */}
              <Card className="p-4 bg-slate-800 border-slate-700">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-yellow-400" />
                  Licencias por Vencer
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {[...(dashboard?.alertas?.clubes_por_vencer || []).map(c => ({...c, tipo: 'club'})),
                    ...(dashboard?.alertas?.academias_por_vencer || []).map(a => ({...a, tipo: 'academia'}))
                  ].length === 0 ? (
                    <p className="text-slate-400 text-sm">No hay licencias por vencer</p>
                  ) : (
                    [...(dashboard?.alertas?.clubes_por_vencer || []).map(c => ({...c, tipo: 'club'})),
                      ...(dashboard?.alertas?.academias_por_vencer || []).map(a => ({...a, tipo: 'academia'}))
                    ].map(org => (
                      <div key={org.id} className="flex items-center justify-between p-2 bg-yellow-500/10 rounded-lg">
                        <div className="flex items-center gap-2">
                          {org.tipo === 'club' ? <Users className="w-4 h-4 text-purple-400" /> : <GraduationCap className="w-4 h-4 text-emerald-400" />}
                          <span className="text-sm">{org.nombre}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-yellow-400">{org.plan_valido_hasta}</span>
                          <Button size="sm" className="h-7 text-xs" onClick={() => abrirDialogPago(org)}>
                            <Plus className="w-3 h-3 mr-1" /> Renovar
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Card>

              {/* Vencidos */}
              <Card className="p-4 bg-slate-800 border-slate-700">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-400" />
                    Licencias Vencidas
                  </h3>
                  <Button size="sm" variant="outline" onClick={handleVerificarLicencias}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Verificar
                  </Button>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {[...(dashboard?.alertas?.clubes_vencidos || []).map(c => ({...c, tipo: 'club'})),
                    ...(dashboard?.alertas?.academias_vencidas || []).map(a => ({...a, tipo: 'academia'}))
                  ].length === 0 ? (
                    <p className="text-slate-400 text-sm">No hay licencias vencidas</p>
                  ) : (
                    [...(dashboard?.alertas?.clubes_vencidos || []).map(c => ({...c, tipo: 'club'})),
                      ...(dashboard?.alertas?.academias_vencidas || []).map(a => ({...a, tipo: 'academia'}))
                    ].map(org => (
                      <div key={org.id} className="flex items-center justify-between p-2 bg-red-500/10 rounded-lg">
                        <div className="flex items-center gap-2">
                          {org.tipo === 'club' ? <Users className="w-4 h-4 text-purple-400" /> : <GraduationCap className="w-4 h-4 text-emerald-400" />}
                          <span className="text-sm">{org.nombre}</span>
                        </div>
                        <span className="text-xs text-red-400">Expiró: {org.plan_valido_hasta}</span>
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </div>

            {/* Pagos Recientes */}
            <Card className="p-4 bg-slate-800 border-slate-700">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-400" />
                Últimos Pagos
              </h3>
              <div className="space-y-2">
                {(dashboard?.pagos_recientes || []).slice(0, 5).map(pago => (
                  <div key={pago.id} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {pago.tipo === 'club' ? <Users className="w-4 h-4 text-purple-400" /> : <GraduationCap className="w-4 h-4 text-emerald-400" />}
                      <div>
                        <p className="text-sm font-medium">{pago.organizacion_nombre}</p>
                        <p className="text-xs text-slate-400">{pago.fecha_pago} • {pago.metodo_pago}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {pago.monto_clp > 0 && <p className="text-emerald-400 font-medium">{formatMoney(pago.monto_clp)}</p>}
                      {pago.monto_usd > 0 && <p className="text-blue-400 font-medium">${pago.monto_usd} USD</p>}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          {/* Organizaciones Tab */}
          <TabsContent value="organizaciones" className="space-y-4">
            <div className="flex gap-4 items-center">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por nombre o email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-800 border-slate-700"
                />
              </div>
              <Select value={filtroTipo} onValueChange={(v) => { setFiltroTipo(v); }}>
                <SelectTrigger className="w-40 bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas</SelectItem>
                  <SelectItem value="clubes">Solo Clubes</SelectItem>
                  <SelectItem value="academias">Solo Academias</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={loadData} variant="outline" size="icon">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-3">
              {filteredOrgs.map(org => {
                const estadoLicencia = getEstadoLicencia(org);
                return (
                  <Card key={org.id} className="p-4 bg-slate-800 border-slate-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${org.tipo === 'club' ? 'bg-purple-500/20' : 'bg-emerald-500/20'}`}>
                          {org.tipo === 'club' ? <Users className="w-6 h-6 text-purple-400" /> : <GraduationCap className="w-6 h-6 text-emerald-400" />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{org.nombre}</h3>
                            <Badge className={`${estadoLicencia.color} text-xs`}>{org.plan?.toUpperCase()}</Badge>
                          </div>
                          <p className="text-sm text-slate-400">{org.admin_info?.email || 'Sin admin'}</p>
                          <p className="text-xs text-slate-500">{estadoLicencia.texto}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" onClick={() => abrirDialogPago(org)}>
                          <DollarSign className="w-4 h-4 mr-1" /> Registrar Pago
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Pagos Tab */}
          <TabsContent value="pagos" className="space-y-4">
            <Card className="bg-slate-800 border-slate-700">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left p-4 text-slate-400 font-medium">Fecha</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Organización</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Tipo</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Monto</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Método</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Nueva Expiración</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Registrado por</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagos.map(pago => (
                      <tr key={pago.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="p-4">{pago.fecha_pago}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            {pago.tipo === 'club' ? <Users className="w-4 h-4 text-purple-400" /> : <GraduationCap className="w-4 h-4 text-emerald-400" />}
                            {pago.organizacion_nombre}
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge className={pago.es_primera_vez ? 'bg-blue-500' : 'bg-slate-600'}>
                            {pago.es_primera_vez ? 'Primera vez' : 'Renovación'}
                          </Badge>
                        </td>
                        <td className="p-4">
                          {pago.monto_clp > 0 && <span className="text-emerald-400">{formatMoney(pago.monto_clp)}</span>}
                          {pago.monto_clp > 0 && pago.monto_usd > 0 && ' / '}
                          {pago.monto_usd > 0 && <span className="text-blue-400">${pago.monto_usd} USD</span>}
                        </td>
                        <td className="p-4 text-slate-400">{pago.metodo_pago}</td>
                        <td className="p-4">{pago.nueva_expiracion}</td>
                        <td className="p-4 text-slate-400 text-sm">{pago.registrado_por}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* Clubes Tab */}
          <TabsContent value="clubes" className="space-y-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por nombre, email o código..."
                  value={filtroClubes.search}
                  onChange={(e) => setFiltroClubes({ ...filtroClubes, search: e.target.value })}
                  className="pl-10 bg-slate-800 border-slate-700"
                />
              </div>
              <Select value={filtroClubes.plan} onValueChange={(v) => setFiltroClubes({ ...filtroClubes, plan: v })}>
                <SelectTrigger className="w-32 bg-slate-800 border-slate-700">
                  <SelectValue placeholder="Plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={loadClubes} variant="outline" size="icon" disabled={loadingClubes}>
                <RefreshCw className={`w-4 h-4 ${loadingClubes ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {loadingClubes ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
              </div>
            ) : (
              <Card className="bg-slate-800 border-slate-700">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left p-4 text-slate-400 font-medium">Nombre</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Código</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Admin</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Integrantes</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Plan</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Creado</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredClubes.map(club => (
                        <tr key={club.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-purple-400" />
                              <span className="font-medium">{club.nombre}</span>
                            </div>
                          </td>
                          <td className="p-4 text-slate-400 font-mono text-sm">{club.codigo_pago || '-'}</td>
                          <td className="p-4">
                            <div>
                              <p className="text-sm">{club.admin_info?.email || 'Sin admin'}</p>
                              {club.admin_info?.email_verified !== undefined && (
                                <Badge className={`mt-1 text-xs ${club.admin_info.email_verified ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                                  {club.admin_info.email_verified ? 'Verificado' : 'No verificado'}
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <span>{club.total_integrantes || 0}</span>
                              {(club.integrantes_sin_categoria || 0) > 0 && (
                                <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                                  {club.integrantes_sin_categoria} sin cat.
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <Badge className={club.plan === 'pro' ? 'bg-emerald-500' : 'bg-slate-600'}>
                              {club.plan?.toUpperCase() || 'FREE'}
                            </Badge>
                            {club.plan === 'pro' && club.plan_valido_hasta && (
                              <p className="text-xs text-slate-400 mt-1">{club.plan_valido_hasta}</p>
                            )}
                          </td>
                          <td className="p-4 text-slate-400 text-sm">
                            {club.created_at ? new Date(club.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="p-4">
                            <div className="flex gap-1 flex-wrap">
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-emerald-600/50 text-emerald-400 hover:bg-emerald-600/20 h-8 text-xs"
                                onClick={() => abrirGestionPlan(club, 'club')}
                                data-testid={`plan-club-${club.id}`}
                              >
                                <ShieldCheck className="w-3 h-3 mr-1" />
                                Plan
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-blue-600/50 text-blue-400 hover:bg-blue-600/20 h-8 text-xs"
                                onClick={() => handleDiagIntegrantes(club.id)}
                                data-testid={`diag-club-${club.id}`}
                              >
                                <Search className="w-3 h-3 mr-1" />
                                Revisar
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-red-600/50 text-red-400 hover:bg-red-600/20 h-8 text-xs"
                                onClick={() => iniciarEliminacion('club', club.id, club.nombre)}
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                Eliminar
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {filteredClubes.length === 0 && (
                        <tr>
                          <td colSpan="7" className="p-8 text-center text-slate-400">No se encontraron clubes</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* Academias Tab */}
          <TabsContent value="academias" className="space-y-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por nombre, email o código..."
                  value={filtroAcademias.search}
                  onChange={(e) => setFiltroAcademias({ ...filtroAcademias, search: e.target.value })}
                  className="pl-10 bg-slate-800 border-slate-700"
                />
              </div>
              <Select value={filtroAcademias.plan} onValueChange={(v) => setFiltroAcademias({ ...filtroAcademias, plan: v })}>
                <SelectTrigger className="w-32 bg-slate-800 border-slate-700">
                  <SelectValue placeholder="Plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={loadAcademias} variant="outline" size="icon" disabled={loadingAcademias}>
                <RefreshCw className={`w-4 h-4 ${loadingAcademias ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {loadingAcademias ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
              </div>
            ) : (
              <Card className="bg-slate-800 border-slate-700">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left p-4 text-slate-400 font-medium">Nombre</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Código</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Admin</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Alumnos</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Plan</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Creado</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAcademias.map(academia => (
                        <tr key={academia.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <GraduationCap className="w-4 h-4 text-emerald-400" />
                              <span className="font-medium">{academia.nombre}</span>
                            </div>
                          </td>
                          <td className="p-4 text-slate-400 font-mono text-sm">{academia.codigo_pago || '-'}</td>
                          <td className="p-4">
                            <div>
                              <p className="text-sm">{academia.admin_info?.email || 'Sin admin'}</p>
                              {academia.admin_info?.email_verified !== undefined && (
                                <Badge className={`mt-1 text-xs ${academia.admin_info.email_verified ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                                  {academia.admin_info.email_verified ? 'Verificado' : 'No verificado'}
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="p-4">{academia.total_alumnos || 0}</td>
                          <td className="p-4">
                            <Badge className={academia.plan === 'pro' ? 'bg-emerald-500' : 'bg-slate-600'}>
                              {academia.plan?.toUpperCase() || 'FREE'}
                            </Badge>
                          </td>
                          <td className="p-4 text-slate-400 text-sm">
                            {academia.created_at ? new Date(academia.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="p-4">
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="border-red-600/50 text-red-400 hover:bg-red-600/20 h-8 text-xs"
                              onClick={() => iniciarEliminacion('academia', academia.id, academia.nombre)}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Eliminar
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {filteredAcademias.length === 0 && (
                        <tr>
                          <td colSpan="7" className="p-8 text-center text-slate-400">No se encontraron academias</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* Entrenadores Tab */}
          <TabsContent value="entrenadores" className="space-y-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por nombre, email o academia..."
                  value={filtroEntrenadores.search}
                  onChange={(e) => setFiltroEntrenadores({ ...filtroEntrenadores, search: e.target.value })}
                  className="pl-10 bg-slate-800 border-slate-700"
                />
              </div>
              <Select value={filtroEntrenadores.estado} onValueChange={(v) => setFiltroEntrenadores({ ...filtroEntrenadores, estado: v })}>
                <SelectTrigger className="w-32 bg-slate-800 border-slate-700">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="activo">Activos</SelectItem>
                  <SelectItem value="inactivo">Inactivos</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={loadEntrenadores} variant="outline" size="icon" disabled={loadingEntrenadores}>
                <RefreshCw className={`w-4 h-4 ${loadingEntrenadores ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {loadingEntrenadores ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
              </div>
            ) : (
              <Card className="bg-slate-800 border-slate-700">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left p-4 text-slate-400 font-medium">Nombre</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Email</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Academia</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Estado</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Invitación</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Email Verificado</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEntrenadores.map(entrenador => (
                        <tr key={entrenador.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <Dumbbell className="w-4 h-4 text-amber-400" />
                              <span className="font-medium">{entrenador.nombre} {entrenador.apellido}</span>
                              {entrenador.es_principal && (
                                <Badge className="bg-amber-500/20 text-amber-400 text-xs">Principal</Badge>
                              )}
                            </div>
                          </td>
                          <td className="p-4 text-slate-400">{entrenador.email}</td>
                          <td className="p-4">{entrenador.academia_nombre}</td>
                          <td className="p-4">
                            <Badge className={entrenador.estado === 'activo' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600'}>
                              {entrenador.estado || 'activo'}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <Badge className={entrenador.invitacion_aceptada ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'}>
                              {entrenador.invitacion_aceptada ? 'Aceptada' : entrenador.invitacion_enviada ? 'Pendiente' : 'No enviada'}
                            </Badge>
                          </td>
                          <td className="p-4">
                            {entrenador.usuario_info ? (
                              <Badge className={`text-xs ${entrenador.usuario_info.email_verified ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                                {entrenador.usuario_info.email_verified ? 'Verificado' : 'No verificado'}
                              </Badge>
                            ) : (
                              <span className="text-slate-500 text-sm">Sin cuenta</span>
                            )}
                          </td>
                          <td className="p-4">
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="border-red-600/50 text-red-400 hover:bg-red-600/20 h-8 text-xs"
                              onClick={() => iniciarEliminacion('entrenador', entrenador.id, `${entrenador.nombre} ${entrenador.apellido}`)}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Eliminar
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {filteredEntrenadores.length === 0 && (
                        <tr>
                          <td colSpan="7" className="p-8 text-center text-slate-400">No se encontraron entrenadores</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* Usuarios Tab */}
          <TabsContent value="usuarios" className="space-y-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por nombre o email..."
                  value={filtroUsuarios.search}
                  onChange={(e) => setFiltroUsuarios({ ...filtroUsuarios, search: e.target.value })}
                  className="pl-10 bg-slate-800 border-slate-700"
                />
              </div>
              <Select value={filtroUsuarios.rol} onValueChange={(v) => setFiltroUsuarios({ ...filtroUsuarios, rol: v })}>
                <SelectTrigger className="w-36 bg-slate-800 border-slate-700">
                  <SelectValue placeholder="Rol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los roles</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="super_user">Super Usuario</SelectItem>
                  <SelectItem value="turno">Turno</SelectItem>
                  <SelectItem value="entrenador">Entrenador</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filtroUsuarios.verificado} onValueChange={(v) => setFiltroUsuarios({ ...filtroUsuarios, verificado: v })}>
                <SelectTrigger className="w-40 bg-slate-800 border-slate-700">
                  <SelectValue placeholder="Verificado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="si">Verificados</SelectItem>
                  <SelectItem value="no">No verificados</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={loadUsuarios} variant="outline" size="icon" disabled={loadingUsuarios}>
                <RefreshCw className={`w-4 h-4 ${loadingUsuarios ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {loadingUsuarios ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
              </div>
            ) : (
              <Card className="bg-slate-800 border-slate-700">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left p-4 text-slate-400 font-medium">Nombre</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Email</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Rol</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Organizaciones</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Email Verificado</th>
                        <th className="text-left p-4 text-slate-400 font-medium">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsuarios.map(usuario => (
                        <tr key={usuario.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-blue-400" />
                              <span className="font-medium">{usuario.name || 'Sin nombre'}</span>
                            </div>
                          </td>
                          <td className="p-4 text-slate-400 text-sm">{usuario.email}</td>
                          <td className="p-4">
                            <Badge className={
                              usuario.es_admin_kinosis ? 'bg-red-500/20 text-red-400' :
                              usuario.role === 'entrenador' ? 'bg-amber-500/20 text-amber-400' :
                              'bg-slate-600'
                            }>
                              {usuario.es_admin_kinosis ? 'Admin Kinosis' : 
                               usuario.role === 'entrenador' ? 'Entrenador' : 'Usuario'}
                            </Badge>
                          </td>
                          <td className="p-4">
                            {usuario.relaciones && usuario.relaciones.length > 0 ? (
                              <div className="space-y-1">
                                {usuario.relaciones.map((rel, idx) => (
                                  <div key={idx} className="flex items-center gap-2 text-sm">
                                    {rel.tipo === 'club' ? (
                                      <Users className="w-3 h-3 text-purple-400" />
                                    ) : (
                                      <GraduationCap className="w-3 h-3 text-emerald-400" />
                                    )}
                                    <span className="text-slate-300">{rel.organizacion_nombre}</span>
                                    <Badge className={
                                      rel.es_creador ? 'text-xs bg-emerald-500/20 text-emerald-400' :
                                      rel.rol === 'super_usuario' || rel.rol === 'admin' ? 'text-xs bg-blue-500/20 text-blue-400' :
                                      'text-xs bg-slate-600'
                                    }>
                                      {rel.es_creador ? 'Creador' : 
                                       rel.rol === 'super_usuario' ? 'Super Usuario' :
                                       rel.rol === 'admin' ? 'Admin' :
                                       rel.rol === 'lectura' ? 'Solo Lectura' : rel.rol}
                                    </Badge>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-5 w-5 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                                      onClick={async () => {
                                        if (confirm(`¿Remover acceso de ${usuario.name || usuario.email} a ${rel.organizacion_nombre}?`)) {
                                          try {
                                            await axios.delete(
                                              `${API}/backoffice/usuarios/${usuario.id}/relacion/${rel.tipo}/${rel.relacion_id}`,
                                              { withCredentials: true }
                                            );
                                            toast.success('Acceso removido correctamente');
                                            loadUsuarios();
                                          } catch (error) {
                                            toast.error('Error al remover acceso');
                                          }
                                        }
                                      }}
                                      title="Remover acceso"
                                    >
                                      <XCircle className="w-3 h-3" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-slate-500 text-sm">Sin organizaciones</span>
                            )}
                          </td>
                          <td className="p-4">
                            <Badge className={`text-xs ${usuario.email_verified ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                              {usuario.email_verified ? 'Verificado' : 'No verificado'}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              {!usuario.email_verified && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="border-emerald-600/50 text-emerald-400 hover:bg-emerald-600/20 h-8 text-xs"
                                  onClick={() => handleVerificarEmailUsuario(usuario.id, usuario.email)}
                                >
                                  <MailCheck className="w-3 h-3 mr-1" />
                                  Verificar
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-red-600/50 text-red-400 hover:bg-red-600/20 h-8 text-xs"
                                onClick={() => iniciarEliminacion('usuario', usuario.id, usuario.name || usuario.email, { email: usuario.email })}
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                Eliminar
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {filteredUsuarios.length === 0 && (
                        <tr>
                          <td colSpan="6" className="p-8 text-center text-slate-400">No se encontraron usuarios</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* Verificación Manual Tab */}
          <TabsContent value="verificacion" className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-amber-400" />
                Enrolamientos Pendientes de Verificación Manual
              </h3>
              <Button onClick={loadPendientesVerificacion} variant="outline" size="icon" disabled={loadingVerificacion}>
                <RefreshCw className={`w-4 h-4 ${loadingVerificacion ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {loadingVerificacion ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
              </div>
            ) : pendientesVerificacion.length === 0 ? (
              <Card className="p-8 text-center bg-slate-800 border-slate-700">
                <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <p className="text-slate-300 font-medium">Sin pendientes</p>
                <p className="text-sm text-slate-500 mt-1">No hay enrolamientos pendientes de verificación manual.</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {pendientesVerificacion.map(persona => (
                  <Card key={persona.id} className="p-4 bg-slate-800 border-slate-700 border-l-4 border-l-amber-500" data-testid={`verificacion-persona-${persona.id}`}>
                    <div className="flex gap-4">
                      <div className="flex gap-2 flex-shrink-0">
                        {persona.foto_cara_base64 ? (
                          <img src={persona.foto_cara_base64} alt="Selfie" className="w-16 h-22 object-cover rounded-lg border-2 border-slate-600" />
                        ) : (
                          <div className="w-16 h-22 rounded-lg border-2 border-slate-700 bg-slate-900 flex items-center justify-center">
                            <User className="w-6 h-6 text-slate-600" />
                          </div>
                        )}
                        {persona.foto_cedula_base64 ? (
                          <img src={persona.foto_cedula_base64} alt="Cédula" className="w-20 h-22 object-cover rounded-lg border-2 border-slate-600" />
                        ) : (
                          <div className="w-20 h-22 rounded-lg border-2 border-slate-700 bg-slate-900 flex items-center justify-center">
                            <FileText className="w-6 h-6 text-slate-600" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h4 className="font-bold text-white">{persona.nombre} {persona.apellido || ''}</h4>
                          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">Pendiente Verificación</Badge>
                          {persona.fecha_nacimiento_modificada && (
                            <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs">Fecha Nac. Modificada</Badge>
                          )}
                        </div>
                        <div className="space-y-1 text-sm">
                          <p className="text-slate-400">RUT: <span className="text-white font-mono">{persona.rut_formateado || persona.rut}</span></p>
                          <p className="text-slate-400">Fecha Nacimiento: <span className="text-white">{persona.fecha_nacimiento || 'N/A'}</span></p>
                          {persona.fecha_nacimiento_ocr && persona.fecha_nacimiento_ocr !== persona.fecha_nacimiento && (
                            <p className="text-red-400 text-xs">OCR original: {persona.fecha_nacimiento_ocr}</p>
                          )}
                          <p className="text-slate-500 text-xs">
                            Enrolado: {persona.fecha_enrolamiento ? new Date(persona.fecha_enrolamiento).toLocaleString('es-CL') : 'N/A'}
                          </p>
                        </div>
                        <Button size="sm" className="mt-2 bg-amber-600 hover:bg-amber-700 text-white gap-1"
                          onClick={() => abrirVerificacion(persona)} data-testid={`verificar-btn-${persona.id}`}>
                          <ShieldCheck className="w-3.5 h-3.5" /> Revisar
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {/* Eliminar Enrolamiento por RUT */}
            <div className="mt-6 p-4 bg-slate-800 border border-red-900/30 rounded-lg">
              <h4 className="text-sm font-semibold text-red-400 mb-3 flex items-center gap-2">
                <Trash2 className="w-4 h-4" /> Eliminar Enrolamiento por RUT
              </h4>
              <div className="flex gap-2">
                <Input value={rutEliminar} onChange={(e) => setRutEliminar(e.target.value)}
                  placeholder="Ej: 13.074.153-3" className="bg-slate-700 border-slate-600 text-white flex-1"
                  data-testid="rut-eliminar-input" />
                <Button onClick={handleEliminarEnrolamiento}
                  disabled={eliminandoRut || rutEliminar.replace(/[^0-9kK]/g, '').length < 8}
                  variant="destructive" className="gap-1" data-testid="eliminar-enrolamiento-btn">
                  {eliminandoRut ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Eliminar
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">Elimina permanentemente todos los registros de enrolamiento de este RUT.</p>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Dialog Registrar Pago */}
      <Dialog open={showPagoDialog} onOpenChange={setShowPagoDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              Registrar Pago de Licencia
            </DialogTitle>
          </DialogHeader>
          
          {orgSeleccionada && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-700 rounded-lg">
                <div className="flex items-center gap-2">
                  {orgSeleccionada.tipo === 'club' ? <Users className="w-5 h-5 text-purple-400" /> : <GraduationCap className="w-5 h-5 text-emerald-400" />}
                  <div>
                    <p className="font-medium">{orgSeleccionada.nombre}</p>
                    <p className="text-sm text-slate-400">
                      {orgSeleccionada.tipo === 'club' ? 'Club' : 'Academia'} • 
                      {orgSeleccionada.plan_valido_hasta ? ` Expira: ${orgSeleccionada.plan_valido_hasta}` : ' Sin licencia activa'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Monto CLP</Label>
                  <Input
                    type="number"
                    placeholder="Ej: 15990"
                    value={pagoForm.monto_clp}
                    onChange={(e) => setPagoForm({ ...pagoForm, monto_clp: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Monto USD</Label>
                  <Input
                    type="number"
                    placeholder="Ej: 20"
                    value={pagoForm.monto_usd}
                    onChange={(e) => setPagoForm({ ...pagoForm, monto_usd: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-300">Método de Pago</Label>
                <Select value={pagoForm.metodo_pago} onValueChange={(v) => setPagoForm({ ...pagoForm, metodo_pago: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transferencia">Transferencia</SelectItem>
                    <SelectItem value="paypal">PayPal</SelectItem>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-slate-300">Comprobante (opcional)</Label>
                <Input
                  placeholder="Número de comprobante o referencia"
                  value={pagoForm.comprobante}
                  onChange={(e) => setPagoForm({ ...pagoForm, comprobante: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>

              <div>
                <Label className="text-slate-300">Notas (opcional)</Label>
                <Textarea
                  placeholder="Notas adicionales..."
                  value={pagoForm.notas}
                  onChange={(e) => setPagoForm({ ...pagoForm, notas: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                  rows={2}
                />
              </div>

              <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                <p className="text-sm text-emerald-300">
                  {!orgSeleccionada.plan_valido_hasta || !orgSeleccionada.fecha_inicio_licencia
                    ? '✓ Primera contratación: 30 días desde hoy'
                    : '✓ Renovación: 30 días desde la última expiración'
                  }
                </p>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowPagoDialog(false)} className="flex-1 border-slate-600">
                  Cancelar
                </Button>
                <Button onClick={handleRegistrarPago} disabled={guardando} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  {guardando ? 'Guardando...' : 'Registrar Pago'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmación de Eliminación */}
      <Dialog open={showDeleteDialog} onOpenChange={cerrarDeleteDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertOctagon className="w-6 h-6" />
              {deleteStep === 1 ? '¿Eliminar permanentemente?' : 'Confirmación Final'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {deleteStep === 1 
                ? 'Esta acción no se puede deshacer.'
                : 'Escribe el texto de confirmación para proceder.'
              }
            </DialogDescription>
          </DialogHeader>
          
          {deleteTarget && (
            <div className="space-y-4">
              {/* Info del elemento a eliminar */}
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  {deleteTarget.tipo === 'club' && <Users className="w-5 h-5 text-purple-400" />}
                  {deleteTarget.tipo === 'academia' && <GraduationCap className="w-5 h-5 text-emerald-400" />}
                  {deleteTarget.tipo === 'entrenador' && <Dumbbell className="w-5 h-5 text-amber-400" />}
                  {deleteTarget.tipo === 'usuario' && <User className="w-5 h-5 text-blue-400" />}
                  <div>
                    <p className="font-semibold text-white">{deleteTarget.nombre}</p>
                    <p className="text-sm text-slate-400 capitalize">{deleteTarget.tipo}</p>
                  </div>
                </div>
              </div>

              {/* Paso 1: Primera advertencia */}
              {deleteStep === 1 && (
                <>
                  {/* Info de lo que se eliminará (solo para clubs y academias) */}
                  {deleteInfo && (deleteTarget.tipo === 'club' || deleteTarget.tipo === 'academia') && (
                    <div className="p-4 bg-slate-700/50 rounded-lg">
                      <p className="text-sm font-medium text-yellow-400 mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Se eliminarán los siguientes registros:
                      </p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {deleteTarget.tipo === 'club' && (
                          <>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Integrantes:</span>
                              <span className="text-white font-medium">{deleteInfo.integrantes || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Categorías:</span>
                              <span className="text-white font-medium">{deleteInfo.categorias || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Cuotas:</span>
                              <span className="text-white font-medium">{deleteInfo.cuotas || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Pagos:</span>
                              <span className="text-white font-medium">{deleteInfo.pagos || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Ingresos:</span>
                              <span className="text-white font-medium">{deleteInfo.ingresos || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Egresos:</span>
                              <span className="text-white font-medium">{deleteInfo.egresos || 0}</span>
                            </div>
                          </>
                        )}
                        {deleteTarget.tipo === 'academia' && (
                          <>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Alumnos:</span>
                              <span className="text-white font-medium">{deleteInfo.alumnos || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Categorías:</span>
                              <span className="text-white font-medium">{deleteInfo.categorias || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Entrenadores:</span>
                              <span className="text-white font-medium">{deleteInfo.entrenadores || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Clases:</span>
                              <span className="text-white font-medium">{deleteInfo.clases || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Sucursales:</span>
                              <span className="text-white font-medium">{deleteInfo.sucursales || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-400">Cuotas:</span>
                              <span className="text-white font-medium">{deleteInfo.cuotas || 0}</span>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="mt-3 pt-3 border-t border-slate-600 flex justify-between">
                        <span className="text-slate-300 font-medium">Total registros:</span>
                        <span className="text-red-400 font-bold">{deleteInfo.total || 0}</span>
                      </div>
                    </div>
                  )}

                  {(deleteTarget.tipo === 'entrenador') && (
                    <div className="p-4 bg-slate-700/50 rounded-lg">
                      <p className="text-sm text-slate-300">
                        Se eliminará el entrenador y su cuenta de usuario asociada (si existe).
                      </p>
                    </div>
                  )}

                  {(deleteTarget.tipo === 'usuario') && (
                    <div className="p-4 bg-slate-700/50 rounded-lg">
                      <p className="text-sm text-slate-300">
                        Se eliminará el usuario <strong>{deleteTarget.email}</strong> y todas sus sesiones activas.
                      </p>
                    </div>
                  )}

                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-sm text-yellow-300">
                      <strong>⚠️ Advertencia:</strong> Esta acción es <strong>IRREVERSIBLE</strong>. 
                      Todos los datos asociados serán eliminados permanentemente.
                    </p>
                  </div>

                  <DialogFooter className="flex gap-3 sm:gap-3">
                    <Button variant="outline" onClick={cerrarDeleteDialog} className="flex-1 border-slate-600">
                      Cancelar
                    </Button>
                    <Button 
                      onClick={handleConfirmarPaso1} 
                      className="flex-1 bg-red-600 hover:bg-red-700"
                    >
                      Sí, continuar
                    </Button>
                  </DialogFooter>
                </>
              )}

              {/* Paso 2: Segunda confirmación con texto */}
              {deleteStep === 2 && (
                <>
                  <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
                    <p className="text-sm text-red-300 mb-3">
                      Para confirmar la eliminación, escribe exactamente:
                    </p>
                    <p className="font-mono text-lg text-white bg-slate-900 p-2 rounded text-center">
                      ELIMINAR {deleteTarget.nombre.toUpperCase()}
                    </p>
                  </div>

                  <div>
                    <Label className="text-slate-300">Texto de confirmación</Label>
                    <Input
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder={`ELIMINAR ${deleteTarget.nombre.toUpperCase()}`}
                      className="bg-slate-700 border-slate-600 mt-2 font-mono"
                    />
                  </div>

                  <DialogFooter className="flex gap-3 sm:gap-3">
                    <Button variant="outline" onClick={() => setDeleteStep(1)} className="flex-1 border-slate-600">
                      Volver
                    </Button>
                    <Button 
                      onClick={handleConfirmarPaso2} 
                      disabled={loadingDelete || confirmText.toUpperCase() !== `ELIMINAR ${deleteTarget.nombre}`.toUpperCase()}
                      className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50"
                    >
                      {loadingDelete ? 'Eliminando...' : 'Eliminar Definitivamente'}
                    </Button>
                  </DialogFooter>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Diagnóstico de Cuotas */}
      <Dialog open={showDiagnosticoDialog} onOpenChange={setShowDiagnosticoDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Diagnóstico de Morosidades
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Detecta cuotas huérfanas que generan morosidades ficticias en el sistema
            </DialogDescription>
          </DialogHeader>
          
          {loadingDiagnostico ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
              <span className="ml-3 text-slate-400">Analizando cuotas...</span>
            </div>
          ) : diagnosticoCuotas ? (
            <div className="space-y-4">
              {/* Resumen */}
              <Card className={`p-4 border ${diagnosticoCuotas.total_problemas > 0 ? 'bg-red-500/10 border-red-500/30' : 'bg-emerald-500/10 border-emerald-500/30'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Cuotas con problemas</p>
                    <p className={`text-3xl font-bold ${diagnosticoCuotas.total_problemas > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                      {diagnosticoCuotas.total_problemas}
                    </p>
                  </div>
                  {diagnosticoCuotas.total_problemas > 0 && (
                    <Button 
                      onClick={handleLimpiarCuotasHuerfanas} 
                      disabled={loadingLimpieza}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {loadingLimpieza ? 'Limpiando...' : 'Limpiar Todo'}
                    </Button>
                  )}
                </div>
              </Card>

              {/* Clubes */}
              <Card className="p-4 bg-slate-700 border-slate-600">
                <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-purple-400" />
                  Clubes
                </h4>
                <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                  <div>
                    <p className="text-slate-400">Sin integrante</p>
                    <p className="text-xl font-bold text-red-400">{diagnosticoCuotas.clubes.cuotas_sin_integrante}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Sin plan</p>
                    <p className="text-xl font-bold text-orange-400">{diagnosticoCuotas.clubes.cuotas_sin_plan}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Club inexistente</p>
                    <p className="text-xl font-bold text-yellow-400">{diagnosticoCuotas.clubes.cuotas_club_inexistente}</p>
                  </div>
                </div>
                {diagnosticoCuotas.clubes.detalles.length > 0 && (
                  <div className="max-h-32 overflow-y-auto space-y-1 bg-slate-800/50 p-2 rounded">
                    {diagnosticoCuotas.clubes.detalles.slice(0, 10).map((d, i) => (
                      <div key={i} className="text-xs flex justify-between">
                        <span className="text-slate-400">{d.descripcion}</span>
                        <span className="text-red-400">{formatMoney(d.monto)}</span>
                      </div>
                    ))}
                    {diagnosticoCuotas.clubes.detalles.length > 10 && (
                      <p className="text-xs text-slate-500 text-center">...y {diagnosticoCuotas.clubes.detalles.length - 10} más</p>
                    )}
                  </div>
                )}
              </Card>

              {/* Academias */}
              <Card className="p-4 bg-slate-700 border-slate-600">
                <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-emerald-400" />
                  Academias
                </h4>
                <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                  <div>
                    <p className="text-slate-400">Sin alumno</p>
                    <p className="text-xl font-bold text-red-400">{diagnosticoCuotas.academias.cuotas_sin_alumno}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Sin plan</p>
                    <p className="text-xl font-bold text-orange-400">{diagnosticoCuotas.academias.cuotas_sin_plan}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Academia inexistente</p>
                    <p className="text-xl font-bold text-yellow-400">{diagnosticoCuotas.academias.cuotas_academia_inexistente}</p>
                  </div>
                </div>
                {diagnosticoCuotas.academias.detalles.length > 0 && (
                  <div className="max-h-32 overflow-y-auto space-y-1 bg-slate-800/50 p-2 rounded">
                    {diagnosticoCuotas.academias.detalles.slice(0, 10).map((d, i) => (
                      <div key={i} className="text-xs flex justify-between">
                        <span className="text-slate-400">{d.descripcion}</span>
                        <span className="text-red-400">{formatMoney(d.monto)}</span>
                      </div>
                    ))}
                    {diagnosticoCuotas.academias.detalles.length > 10 && (
                      <p className="text-xs text-slate-500 text-center">...y {diagnosticoCuotas.academias.detalles.length - 10} más</p>
                    )}
                  </div>
                )}
              </Card>

              {diagnosticoCuotas.total_problemas === 0 && (
                <p className="text-center text-emerald-400 py-4">
                  ✓ No se encontraron cuotas huérfanas. El sistema está limpio.
                </p>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Dialog Verificación Manual */}
      <Dialog open={showVerificacionDialog} onOpenChange={setShowVerificacionDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-amber-400" />
              Verificación Manual de Enrolamiento
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Revisa los datos y las fotos. Haz clic en una foto para ampliarla.
            </DialogDescription>
          </DialogHeader>

          {personaVerificar && (
            <div className="space-y-4">
              {/* Fotos: Selfie + Cédula lado a lado, click para ampliar */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <p className="text-xs text-slate-400 text-center">Selfie</p>
                  {personaVerificar.foto_cara_base64 ? (
                    <img src={personaVerificar.foto_cara_base64} alt="Selfie"
                      onClick={() => setImagenAmpliadaUrl(personaVerificar.foto_cara_base64)}
                      className="w-full h-40 object-cover rounded-lg border-2 border-slate-600 cursor-pointer hover:border-amber-500 transition-colors"
                      data-testid="verificacion-selfie-img" />
                  ) : (
                    <div className="w-full h-40 rounded-lg border-2 border-slate-700 bg-slate-900 flex items-center justify-center">
                      <User className="w-8 h-8 text-slate-600" />
                    </div>
                  )}
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-400 text-center">Cédula</p>
                  {personaVerificar.foto_cedula_base64 ? (
                    <img src={personaVerificar.foto_cedula_base64} alt="Cédula"
                      onClick={() => setImagenAmpliadaUrl(personaVerificar.foto_cedula_base64)}
                      className="w-full h-40 object-cover rounded-lg border-2 border-slate-600 cursor-pointer hover:border-amber-500 transition-colors"
                      data-testid="verificacion-cedula-img" />
                  ) : (
                    <div className="w-full h-40 rounded-lg border-2 border-slate-700 bg-slate-900 flex items-center justify-center">
                      <FileText className="w-8 h-8 text-slate-600" />
                    </div>
                  )}
                </div>
              </div>

              <div className="p-3 bg-slate-700 rounded-lg text-sm space-y-1">
                <p className="text-white font-medium">{personaVerificar.nombre} {personaVerificar.apellido}</p>
                <p className="text-slate-400">RUT: <span className="text-white font-mono">{personaVerificar.rut_formateado || personaVerificar.rut}</span></p>
                {personaVerificar.verificacion_facial !== undefined && (
                  <Badge className={personaVerificar.verificacion_facial ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                    {personaVerificar.verificacion_facial ? 'Facial OK' : 'Sin verificación facial'}
                  </Badge>
                )}
              </div>

              {personaVerificar.fecha_nacimiento_modificada && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-sm text-amber-300 font-medium">Fecha de nacimiento modificada por el usuario</p>
                  {personaVerificar.fecha_nacimiento_ocr && (
                    <p className="text-xs text-slate-400 mt-1">OCR original: <strong className="text-white">{personaVerificar.fecha_nacimiento_ocr}</strong></p>
                  )}
                  <p className="text-xs text-slate-400">Valor actual: <strong className="text-amber-300">{personaVerificar.fecha_nacimiento}</strong></p>
                </div>
              )}

              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300 text-xs">Nombre</Label>
                    <Input value={verificacionForm.nombre} onChange={(e) => setVerificacionForm({...verificacionForm, nombre: e.target.value})}
                      className="bg-slate-700 border-slate-600 text-white h-9" data-testid="verificacion-nombre" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-xs">Apellido</Label>
                    <Input value={verificacionForm.apellido} onChange={(e) => setVerificacionForm({...verificacionForm, apellido: e.target.value})}
                      className="bg-slate-700 border-slate-600 text-white h-9" data-testid="verificacion-apellido" />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Fecha de Nacimiento</Label>
                  <Input value={verificacionForm.fecha_nacimiento} onChange={(e) => setVerificacionForm({...verificacionForm, fecha_nacimiento: e.target.value})}
                    placeholder="yyyy-MM-dd" className="bg-slate-700 border-slate-600 text-white h-9" data-testid="verificacion-fecha" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Motivo de Rechazo (si aplica)</Label>
                  <Input value={verificacionForm.motivo_rechazo} onChange={(e) => setVerificacionForm({...verificacionForm, motivo_rechazo: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white h-9" placeholder="Solo si rechazas..."
                    data-testid="verificacion-motivo-rechazo" />
                </div>
              </div>

              <div className="flex gap-3">
                <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                  onClick={() => handleVerificarPersona('verificar')}
                  disabled={!!verificandoId} data-testid="verificacion-aprobar-btn">
                  <CheckCircle className="w-4 h-4" /> Aprobar
                </Button>
                <Button variant="outline" className="flex-1 border-red-600/50 text-red-400 hover:bg-red-600/20 gap-1"
                  onClick={() => handleVerificarPersona('rechazar')}
                  disabled={!!verificandoId} data-testid="verificacion-rechazar-btn">
                  <XCircle className="w-4 h-4" /> Rechazar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Lightbox para ampliar fotos */}

      {/* Dialog: Gestionar Plan de Organización */}
      <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
              Gestionar Plan
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {planOrg?.nombre} ({planOrg?.tipo === 'club' ? 'Club' : 'Academia'})
            </DialogDescription>
          </DialogHeader>

          {planOrg && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-700/50 rounded-lg">
                <p className="text-sm text-slate-300">
                  Estado actual: <Badge className={planOrg.plan === 'pro' ? 'bg-emerald-500 ml-2' : 'bg-slate-600 ml-2'}>{(planOrg.plan || 'free').toUpperCase()}</Badge>
                </p>
                {planOrg.plan_valido_hasta && (
                  <p className="text-xs text-slate-400 mt-1">Expira: {planOrg.plan_valido_hasta}</p>
                )}
              </div>

              <div>
                <label className="text-sm text-slate-300 block mb-1">Plan</label>
                <Select value={planForm.plan} onValueChange={v => setPlanForm(prev => ({ ...prev, plan: v }))}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {planForm.plan === 'pro' && (
                <div>
                  <label className="text-sm text-slate-300 block mb-1">Válido hasta</label>
                  <Input
                    type="date"
                    value={planForm.plan_valido_hasta}
                    onChange={e => setPlanForm(prev => ({ ...prev, plan_valido_hasta: e.target.value }))}
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="plan-fecha-input"
                  />
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" variant="outline" className="text-xs border-slate-600 text-slate-300"
                      onClick={() => setPlanForm(prev => ({ ...prev, plan_valido_hasta: '2027-03-31' }))}>
                      Marzo 2027
                    </Button>
                    <Button size="sm" variant="outline" className="text-xs border-slate-600 text-slate-300"
                      onClick={() => setPlanForm(prev => ({ ...prev, plan_valido_hasta: '2027-12-31' }))}>
                      Dic 2027
                    </Button>
                    <Button size="sm" variant="outline" className="text-xs border-slate-600 text-slate-300"
                      onClick={() => {
                        const d = new Date(); d.setFullYear(d.getFullYear() + 1);
                        setPlanForm(prev => ({ ...prev, plan_valido_hasta: d.toISOString().split('T')[0] }));
                      }}>
                      +1 Año
                    </Button>
                  </div>
                </div>
              )}

              <div>
                <label className="text-sm text-slate-300 block mb-1">Motivo (opcional)</label>
                <Input
                  placeholder="Ej: Período gratuito, renovación, etc."
                  value={planForm.motivo}
                  onChange={e => setPlanForm(prev => ({ ...prev, motivo: e.target.value }))}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowPlanDialog(false)} className="flex-1 border-slate-600 text-slate-300">
                  Cancelar
                </Button>
                <Button onClick={handleGuardarPlan} disabled={guardandoPlan} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white">
                  {guardandoPlan ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Diagnóstico de integrantes del club */}
      <Dialog open={showDiagIntegrantes} onOpenChange={setShowDiagIntegrantes}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-400" />
              Diagnóstico de Integrantes — {diagIntegrantes?.club_nombre || '...'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Total: {diagIntegrantes?.total_integrantes || 0} integrantes | {diagIntegrantes?.total_categorias || 0} categorías
            </DialogDescription>
          </DialogHeader>

          {loadingDiagIntegrantes ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : diagIntegrantes ? (
            <div className="space-y-6">
              {/* Resumen */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="p-3 bg-slate-800 border-slate-700 text-center">
                  <p className="text-2xl font-bold text-white">{diagIntegrantes.total_integrantes}</p>
                  <p className="text-xs text-slate-400">Total</p>
                </Card>
                <Card className={`p-3 border-slate-700 text-center ${diagIntegrantes.sin_categoria.length > 0 ? 'bg-amber-900/30 border-amber-500/50' : 'bg-slate-800'}`}>
                  <p className={`text-2xl font-bold ${diagIntegrantes.sin_categoria.length > 0 ? 'text-amber-400' : 'text-white'}`}>{diagIntegrantes.sin_categoria.length}</p>
                  <p className="text-xs text-slate-400">Sin categoría</p>
                </Card>
                <Card className={`p-3 border-slate-700 text-center ${diagIntegrantes.duplicados_rut.length > 0 ? 'bg-red-900/30 border-red-500/50' : 'bg-slate-800'}`}>
                  <p className={`text-2xl font-bold ${diagIntegrantes.duplicados_rut.length > 0 ? 'text-red-400' : 'text-white'}`}>{diagIntegrantes.duplicados_rut.length}</p>
                  <p className="text-xs text-slate-400">RUT duplicado</p>
                </Card>
                <Card className={`p-3 border-slate-700 text-center ${diagIntegrantes.duplicados_email.length > 0 ? 'bg-red-900/30 border-red-500/50' : 'bg-slate-800'}`}>
                  <p className={`text-2xl font-bold ${diagIntegrantes.duplicados_email.length > 0 ? 'text-red-400' : 'text-white'}`}>{diagIntegrantes.duplicados_email.length}</p>
                  <p className="text-xs text-slate-400">Email duplicado</p>
                </Card>
              </div>

              {/* Integrantes sin categoría */}
              {diagIntegrantes.sin_categoria.length > 0 && (
                <div>
                  <h3 className="text-amber-400 font-semibold mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Integrantes sin categoría ({diagIntegrantes.sin_categoria.length})
                  </h3>
                  <Card className="bg-slate-800 border-amber-500/30 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-700 bg-slate-700/50">
                          <th className="p-3 text-left text-slate-400">Nombre</th>
                          <th className="p-3 text-left text-slate-400">RUT</th>
                          <th className="p-3 text-left text-slate-400">Email</th>
                          <th className="p-3 text-left text-slate-400">Estado</th>
                          <th className="p-3 text-left text-slate-400">Asignar categoría</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diagIntegrantes.sin_categoria.map(integ => (
                          <tr key={integ.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                            <td className="p-3 text-white font-medium">{integ.apellido}, {integ.nombre}</td>
                            <td className="p-3 text-slate-300 font-mono text-xs">{integ.rut || '-'}</td>
                            <td className="p-3 text-slate-300 text-xs">{integ.email || '-'}</td>
                            <td className="p-3">
                              <Badge className={`text-xs ${(integ.estado || 'activo') === 'activo' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                                {integ.estado || 'activo'}
                              </Badge>
                            </td>
                            <td className="p-3">
                              {diagIntegrantes.categorias.length > 0 ? (
                                <Select onValueChange={(catId) => handleAsignarCategoria(integ.id, catId)} disabled={asignandoCategoria === integ.id}>
                                  <SelectTrigger className="h-8 w-44 bg-slate-700 border-slate-600 text-xs">
                                    <SelectValue placeholder={asignandoCategoria === integ.id ? "Asignando..." : "Seleccionar..."} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {diagIntegrantes.categorias.map(cat => (
                                      <SelectItem key={cat.id} value={cat.id}>{cat.nombre}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <span className="text-xs text-slate-500">No hay categorías</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Card>
                </div>
              )}

              {/* Duplicados por RUT */}
              {diagIntegrantes.duplicados_rut.length > 0 && (
                <div>
                  <h3 className="text-red-400 font-semibold mb-3 flex items-center gap-2">
                    <AlertOctagon className="w-4 h-4" /> RUT duplicados ({diagIntegrantes.duplicados_rut.length} grupos)
                  </h3>
                  {diagIntegrantes.duplicados_rut.map((grupo, idx) => (
                    <Card key={idx} className="bg-slate-800 border-red-500/30 p-3 mb-2">
                      <p className="text-red-400 font-mono text-sm mb-2">RUT: {grupo.rut}</p>
                      <div className="space-y-1">
                        {grupo.integrantes.map(integ => (
                          <div key={integ.id} className="flex items-center gap-3 text-sm text-slate-300 bg-slate-700/50 rounded px-3 py-2">
                            <span className="font-medium text-white">{integ.nombre} {integ.apellido}</span>
                            <span className="text-slate-400">{integ.email || 'sin email'}</span>
                            <Badge className="text-xs bg-slate-600">{(integ.categoria_ids || []).length} cat.</Badge>
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}
                </div>
              )}

              {/* Duplicados por email */}
              {diagIntegrantes.duplicados_email.length > 0 && (
                <div>
                  <h3 className="text-red-400 font-semibold mb-3 flex items-center gap-2">
                    <AlertOctagon className="w-4 h-4" /> Email duplicados ({diagIntegrantes.duplicados_email.length} grupos)
                  </h3>
                  {diagIntegrantes.duplicados_email.map((grupo, idx) => (
                    <Card key={idx} className="bg-slate-800 border-red-500/30 p-3 mb-2">
                      <p className="text-red-400 text-sm mb-2">{grupo.email}</p>
                      <div className="space-y-1">
                        {grupo.integrantes.map(integ => (
                          <div key={integ.id} className="flex items-center gap-3 text-sm text-slate-300 bg-slate-700/50 rounded px-3 py-2">
                            <span className="font-medium text-white">{integ.nombre} {integ.apellido}</span>
                            <span className="text-slate-400 font-mono text-xs">{integ.rut || 'sin rut'}</span>
                            <Badge className="text-xs bg-slate-600">{(integ.categoria_ids || []).length} cat.</Badge>
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}
                </div>
              )}

              {/* Todo bien */}
              {diagIntegrantes.sin_categoria.length === 0 && diagIntegrantes.duplicados_rut.length === 0 && diagIntegrantes.duplicados_email.length === 0 && (
                <Card className="p-6 bg-emerald-900/20 border-emerald-500/30 text-center">
                  <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                  <p className="text-emerald-400 font-medium">Todo en orden</p>
                  <p className="text-slate-400 text-sm">No se encontraron problemas con los integrantes.</p>
                </Card>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      {imagenAmpliadaUrl && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setImagenAmpliadaUrl(null)} data-testid="lightbox-overlay">
          <img src={imagenAmpliadaUrl} alt="Ampliada"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()} />
          <button onClick={() => setImagenAmpliadaUrl(null)}
            className="absolute top-4 right-4 text-white bg-slate-800/80 rounded-full w-10 h-10 flex items-center justify-center hover:bg-slate-700 text-xl"
            data-testid="lightbox-close-btn">
            &times;
          </button>
        </div>
      )}
    </div>
  );
}
